// Formatting helpers for evaluated loop expression values.
// Example: `3px` and `name-state` strings are emitted here.
import type { LoopExpressionValue } from './placeholder-expression-types'

// Format one value into its final string output.
export function formatExpressionValue(value: LoopExpressionValue): string {
  // Return numbers with compact formatting and their original unit.
  return value.kind === 'number' ? `${formatNumericValue(value.value)}${value.unit}` : value.value
}

// Resolve the result unit for numeric addition and subtraction.
export function resolveAddSubUnit(leftUnit: string, rightUnit: string): string | null {
  // Check whether both operands are unitless.
  if (!leftUnit && !rightUnit) {
    // Return the shared unitless result.
    return ''
  }

  // Check whether only the left side has a unit.
  if (leftUnit && !rightUnit) {
    // Return the left-side unit.
    return leftUnit
  }

  // Check whether only the right side has a unit.
  if (!leftUnit && rightUnit) {
    // Return the right-side unit.
    return rightUnit
  }

  // Return a shared unit only when both sides match.
  return leftUnit === rightUnit ? leftUnit : null
}

// Resolve the result unit for numeric division.
export function resolveDivisionUnit(leftUnit: string, rightUnit: string): string | null {
  // Check whether the divisor is unitless.
  if (!rightUnit) {
    // Return the dividend unit unchanged.
    return leftUnit
  }

  // Check whether both division units cancel out.
  if (leftUnit === rightUnit) {
    // Return the unitless division result.
    return ''
  }

  // Return null for unsupported division unit pairs.
  return null
}

// Format numbers without trailing float artifacts.
function formatNumericValue(value: number): string {
  const rounded = Number.parseFloat(value.toFixed(6))
  // Return a compact decimal or integer string.
  return Number.isInteger(rounded) ? String(rounded) : String(rounded)
}
